export type LeanCommunicationLog = {
  id?: string;
  provider?: string | null;
  status?: string | null;
  recipientProfileId?: string | null;
  email?: string | null;
  stats?: {
    opened?: number;
    clicked?: number;
    bounced?: number;
    delivered?: number;
    spam?: number;
  } | null;
};

export type CommunicationLogStats = {
  allSent: number;
  opened: number;
  clicked: number;
  bounced: number;
  delivered: number;
  spam: number;
  notOpened: number;
  failed: number;
  totalOpens: number;
  totalClicks: number;
};

const EMPTY_STATS: CommunicationLogStats = {
  allSent: 0,
  opened: 0,
  clicked: 0,
  bounced: 0,
  delivered: 0,
  spam: 0,
  notOpened: 0,
  failed: 0,
  totalOpens: 0,
  totalClicks: 0,
};

export function emptyCommunicationLogStats(): CommunicationLogStats {
  return { ...EMPTY_STATS };
}

export function statsFromEmailAgg(row: {
  allSent?: number;
  failed?: number;
  totalOpens?: number;
  totalClicks?: number;
  bounced?: number;
  delivered?: number;
  spam?: number;
  openedCount?: number;
  clickedCount?: number;
}): CommunicationLogStats {
  const stats = emptyCommunicationLogStats();
  stats.allSent = Number(row.allSent ?? 0);
  stats.failed = Number(row.failed ?? 0);
  stats.totalOpens = Number(row.totalOpens ?? 0);
  stats.totalClicks = Number(row.totalClicks ?? 0);
  stats.bounced = Number(row.bounced ?? 0);
  stats.delivered = Number(row.delivered ?? 0);
  stats.spam = Number(row.spam ?? 0);
  stats.opened = Number(row.openedCount ?? 0);
  stats.clicked = Number(row.clickedCount ?? 0);
  stats.notOpened = Math.max(0, stats.allSent - stats.opened);
  return stats;
}

export function statsFromPushAgg(row: {
  allSent?: number;
  delivered?: number;
  failed?: number;
}): CommunicationLogStats {
  const stats = emptyCommunicationLogStats();
  stats.allSent = Number(row.allSent ?? 0);
  stats.delivered = Number(row.delivered ?? 0);
  stats.failed = Number(row.failed ?? 0);
  return stats;
}

export function aggregateCommunicationLogs(
  logs: LeanCommunicationLog[],
  isPush: boolean
): CommunicationLogStats {
  const openedKeys = new Set<string>();
  const clickedKeys = new Set<string>();
  let totalOpens = 0;
  let totalClicks = 0;
  const agg = emptyCommunicationLogStats();

  if (!isPush) {
    for (const log of logs) {
      const isSent =
        log.status === "SENT" || (log.stats && (log.stats.delivered ?? 0) > 0);
      if (isSent) agg.allSent += 1;
      if (log.status === "FAILED") agg.failed += 1;
      if (!log.stats) continue;
      const key = log.recipientProfileId || log.email || log.id || "";
      const opens = log.stats.opened || 0;
      const clicks = log.stats.clicked || 0;
      if (opens > 0 && key) openedKeys.add(key);
      if (clicks > 0 && key) clickedKeys.add(key);
      totalOpens += opens;
      totalClicks += clicks;
      agg.bounced += log.stats.bounced || 0;
      agg.delivered += log.stats.delivered || 0;
      agg.spam += log.stats.spam || 0;
    }
  } else {
    const byRecipient = new Map<
      string,
      { inAppOk: boolean; expoOk: boolean; expoFailed: boolean }
    >();
    for (const log of logs) {
      const key = log.recipientProfileId || log.email || log.id || "";
      if (!key) continue;
      const cur = byRecipient.get(key) ?? {
        inAppOk: false,
        expoOk: false,
        expoFailed: false,
      };
      if (
        log.provider === "IN_APP" &&
        (log.status === "DELIVERED" || log.status === "SENT")
      ) {
        cur.inAppOk = true;
      }
      if (log.provider === "EXPO" && log.status === "SENT") cur.expoOk = true;
      if (log.provider === "EXPO" && log.status === "FAILED") {
        cur.expoFailed = true;
      }
      byRecipient.set(key, cur);
    }
    for (const cur of byRecipient.values()) {
      if (cur.expoOk) agg.allSent += 1;
      if (cur.inAppOk) agg.delivered += 1;
      if (cur.expoFailed) agg.failed += 1;
    }
  }

  agg.opened = openedKeys.size;
  agg.clicked = clickedKeys.size;
  agg.totalOpens = totalOpens;
  agg.totalClicks = totalClicks;
  agg.notOpened = Math.max(0, agg.allSent - agg.opened);
  return agg;
}
